import { FollowUpActionStatusEnum } from '@lmc/validations'
import dayjs from 'dayjs'
import type { AttachmentsValue } from '../../attachments/caseAttachments'
import type { IncidentCaseV2StepValues } from '../../incidentCaseV2Steps'
import { fromDateOnlyIso } from '../../stepValues'

/** One action as the step saves it; also the list's own state. */
export type FollowUpActionValue = NonNullable<
  IncidentCaseV2StepValues['followUpActions']
>['actions'][number] & {
  /** Never sent with the step: uploaded and deleted around its save. */
  attachments?: AttachmentsValue
}

const isFinished = (status: FollowUpActionStatusEnum) =>
  status === FollowUpActionStatusEnum.completed ||
  status === FollowUpActionStatusEnum.skipped

/** Past its due date and not yet completed or skipped. */
export const isOverdue = (action: FollowUpActionValue): boolean => {
  const dueDate = fromDateOnlyIso(action.dueDate)
  return (
    !!dueDate &&
    !isFinished(action.status) &&
    dueDate.isBefore(dayjs().startOf('day'))
  )
}

/**
 * When the action was completed: kept if it already was, now if it has just
 * been marked completed, and cleared otherwise. The server does not stamp it.
 */
export const completedAtFor = (
  status: FollowUpActionStatusEnum,
  previous: FollowUpActionValue | undefined
): string | null => {
  if (status !== FollowUpActionStatusEnum.completed) {
    return null
  }
  return previous?.status === FollowUpActionStatusEnum.completed &&
    previous.completedAt
    ? previous.completedAt
    : new Date().toISOString()
}

/**
 * An action as it is sent: its id if saved (a new one has none), no blank
 * details, every nullable field present.
 */
export const toActionRequest = (
  action: FollowUpActionValue
): FollowUpActionValue => ({
  ...(action.id ? { id: action.id } : {}),
  title: action.title,
  status: action.status,
  assignedUserId: action.assignedUserId ?? null,
  ...(action.details ? { details: action.details } : {}),
  dueDate: action.dueDate ?? null,
  completedAt: action.completedAt ?? null
})
